import { Unit } from './types';

export const DAILY_LIFE: Unit = {
  id: 'daily-life',
  number: 5,
  title: 'Daily Life',
  description: 'Get by outside work: shopping, getting around, the hospital, the bank and food.',
  emoji: '🛒',
  lessons: [
    {
      id: 'shopping',
      title: 'Shopping',
      summary: 'How much is it? This one, please',
      exercises: [
        { kind: 'teach', ko: '가게', roman: 'ga-ge', en: 'shop', picture: '🏪' },
        { kind: 'teach', ko: '얼마예요?', roman: 'eol-ma-ye-yo?', en: 'How much is it?' },
        { kind: 'teach', ko: '이거 주세요', roman: 'i-geo ju-se-yo', en: 'This one, please', note: '주세요 (ju-se-yo) means "please give me". Put what you want in front of it.' },
        { kind: 'teach', ko: '비싸요', roman: 'bi-ssa-yo', en: "It's expensive" },
        { kind: 'teach', ko: '싸요', roman: 'ssa-yo', en: "It's cheap" },
        { kind: 'choice', prompt: 'You want to know the price. What do you ask?', options: ['얼마예요?', '이거 주세요', '싸요', '감사합니다'], answer: 0 },
        { kind: 'choice', prompt: 'What does this mean?', ko: '물 주세요', options: ['Water, please', 'The water is cheap', 'Where is the water?', 'I drink water'], answer: 0 },
        { kind: 'choice', prompt: 'Listen. What did you hear?', speak: '비싸요', listenOnly: true, options: ["It's expensive", "It's cheap", 'How much is it?', 'This one, please'], answer: 0 },
        { kind: 'match', prompt: 'Match the phrase to its meaning', pairs: [['가게', 'shop'], ['싸요', 'cheap'], ['비싸요', 'expensive'], ['주세요', 'please give me']] },
      ],
    },
    {
      id: 'transport',
      title: 'Transport',
      summary: 'Bus, subway, taxi, station and ticket',
      exercises: [
        { kind: 'teach', ko: '버스', roman: 'beo-seu', en: 'bus', picture: '🚌' },
        { kind: 'teach', ko: '지하철', roman: 'ji-ha-cheol', en: 'subway', picture: '🚇' },
        { kind: 'teach', ko: '택시', roman: 'taek-si', en: 'taxi', picture: '🚕' },
        { kind: 'teach', ko: '역', roman: 'yeok', en: 'station', picture: '🚉' },
        { kind: 'teach', ko: '표', roman: 'pyo', en: 'ticket', picture: '🎫' },
        { kind: 'teach', ko: '어디예요?', roman: 'eo-di-ye-yo?', en: 'Where is it?', note: '역이 어디예요? "Where is the station?"' },
        { kind: 'choice', prompt: 'Listen. Which picture matches?', speak: '지하철', listenOnly: true, options: ['🚇', '🚌', '🚕', '🎫'], pictureOptions: true, answer: 0, explain: '지하철 (ji-ha-cheol) is the subway.' },
        { kind: 'choice', prompt: 'What does this mean?', ko: '역이 어디예요?', options: ['Where is the station?', 'How much is the ticket?', 'The bus is here', 'I take a taxi'], answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['버스', 'bus'], ['택시', 'taxi'], ['역', 'station'], ['표', 'ticket']] },
      ],
    },
    {
      id: 'hospital',
      title: 'Hospital',
      summary: 'Doctor, medicine and saying where it hurts',
      exercises: [
        { kind: 'teach', ko: '병원', roman: 'byeong-won', en: 'hospital', picture: '🏥' },
        { kind: 'teach', ko: '의사', roman: 'ui-sa', en: 'doctor', picture: '🧑‍⚕️' },
        { kind: 'teach', ko: '약', roman: 'yak', en: 'medicine', picture: '💊' },
        { kind: 'teach', ko: '약국', roman: 'yak-guk', en: 'pharmacy' },
        { kind: 'teach', ko: '아파요', roman: 'a-pa-yo', en: "It hurts / I'm sick" },
        { kind: 'teach', ko: '머리가 아파요', roman: 'meo-ri-ga a-pa-yo', en: 'I have a headache', note: '머리 (meo-ri) is head, 배 (bae) is stomach: 배가 아파요, "my stomach hurts".' },
        { kind: 'choice', prompt: 'Where do you buy medicine?', options: ['약국', '병원', '은행', '역'], answer: 0 },
        { kind: 'choice', prompt: 'Listen. What is wrong?', speak: '배가 아파요', listenOnly: true, options: ['My stomach hurts', 'I have a headache', "I'm hungry", "I'm fine"], answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['병원', 'hospital'], ['의사', 'doctor'], ['약', 'medicine'], ['아파요', 'it hurts']] },
      ],
    },
    {
      id: 'bank',
      title: 'Bank',
      summary: 'Money, card, bankbook and sending money home',
      exercises: [
        { kind: 'teach', ko: '은행', roman: 'eun-haeng', en: 'bank', picture: '🏦' },
        { kind: 'teach', ko: '돈', roman: 'don', en: 'money', picture: '💵' },
        { kind: 'teach', ko: '카드', roman: 'ka-deu', en: 'card', picture: '💳' },
        { kind: 'teach', ko: '통장', roman: 'tong-jang', en: 'bankbook, bank account' },
        { kind: 'teach', ko: '송금', roman: 'song-geum', en: 'sending money (remittance)', note: '고향에 송금해요: "I send money home".' },
        { kind: 'teach', ko: '환전', roman: 'hwan-jeon', en: 'currency exchange' },
        { kind: 'choice', prompt: 'You want to send money to your family. What is it called?', options: ['송금', '환전', '통장', '카드'], answer: 0 },
        { kind: 'choice', prompt: 'Listen. Which picture matches?', speak: '카드', listenOnly: true, options: ['💳', '💵', '🏦', '💊'], pictureOptions: true, answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['은행', 'bank'], ['돈', 'money'], ['통장', 'bankbook'], ['환전', 'exchange']] },
      ],
    },
    {
      id: 'food',
      title: 'Food',
      summary: 'Everyday food, hungry and delicious',
      exercises: [
        { kind: 'teach', ko: '김치', roman: 'gim-chi', en: 'kimchi', picture: '🥬' },
        { kind: 'teach', ko: '라면', roman: 'ra-myeon', en: 'instant noodles', picture: '🍜' },
        { kind: 'teach', ko: '사과', roman: 'sa-gwa', en: 'apple', picture: '🍎' },
        { kind: 'teach', ko: '배고파요', roman: 'bae-go-pa-yo', en: "I'm hungry" },
        { kind: 'teach', ko: '맛있어요', roman: 'ma-si-sseo-yo', en: "It's delicious" },
        { kind: 'choice', prompt: 'Which picture is 라면?', ko: '라면', options: ['🍜', '🍎', '🥬', '🍚'], pictureOptions: true, answer: 0 },
        { kind: 'choice', prompt: 'Listen. What does it mean?', speak: '맛있어요', listenOnly: true, options: ["It's delicious", "I'm hungry", "It's expensive", "It's okay"], answer: 0 },
        { kind: 'choice', prompt: "It's lunch time and you haven't eaten. What do you say?", options: ['배고파요', '맛있어요', '고장 났어요', '비싸요'], answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['사과', 'apple'], ['김치', 'kimchi'], ['밥', 'rice'], ['우유', 'milk']] },
      ],
    },
  ],
};
